import { useNavigate } from "react-router-dom";
import "./style.css"
import Screen from "../Layouts/Screen/Screen";
import { Helmet } from "react-helmet-async";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMessage } from "../../global-components/src";
import { LoadingThreeCircle } from "../../global-components/src/components/spinner/spiner";
import axios from "axios";
import { useAuth } from "../../global-components/src";
import { ChevronDown, ChevronUp, Trash2 } from "react-feather";
import { Session as SessionType } from "../../types";
import { errorMessage } from "../../utils/TextManagerTwo";
import ConfirmationModal from "../../components/Modals/ConfirmationModal";
import { format } from 'date-fns';
import { UAParser } from 'ua-parser-js';
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { canManageAccountSecurity } from "../../utils/account-security";
import { useTranslation } from "react-i18next";
import { Globe, MapPin, Monitor, Smartphone, Tablet, Clock } from "lucide-react";

const Sessions = () => {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const endOfListRef = useRef<HTMLDivElement | null>(null);
    const [sessions, setSessions] = useState<SessionType[] | null>(null);
    const [sessionToDelete, setsessionToDelete] = useState<SessionType | null>(null);
    const { setMessage } = useMessage();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [end, setEnd] = useState(false);
    const [page, setPage] = useState(() => { return 1; });
    const { token, user, logout } = useAuth();
    const canManageSecurity = canManageAccountSecurity(user?.created_at);
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [password, setPassword] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
    const [errorModal, setErrorModalPassword] = useState('');
    const closeRef = useRef<HTMLButtonElement | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const toggleExpand = (id: number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const getDeviceIcon = (platform: string) => {
        const p = (platform || '').toLowerCase();
        if (p.includes('android') || p.includes('ios') || p.includes('iphone') || p.includes('ipad'))
            return <Smartphone size={20} className="ses-device-icon" />;
        if (p.includes('tablet'))
            return <Tablet size={20} className="ses-device-icon" />;
        return <Monitor size={20} className="ses-device-icon" />;
    };


        const formatSession = (session: SessionType): SessionType => {
            if (!session.user_agent.includes("ReadBook")) {
                const parser = UAParser(session.user_agent);
                const browser = parser.browser;
                const os = parser.os;

                return {
                    ...session,
                    browser: `${browser.name} ${browser.version}`,
                    platform: `${os.name} ${os.version}`,
                };
            } else {
                return {
                ...session,
                platform: session.user_agent,
                };
            }
        };


    const loadData = async (newPage?: number) => {
        const currentPage = newPage ?? page + 1; // Usa newPage se fornecido, senão incrementa
        const url = PACOCA_API_URL + `/users/sessions?page=${currentPage}`;

        if (!canManageSecurity || loading || end) return;
        setLoading(true);

        try {
            const response = await axios.get(url, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = response.data;

            if (data.sessions && data.sessions.length > 0) {
                const formattedSessions = data.sessions.map(formatSession);
                // setSessions(formattedSessions);
                setCurrentSessionId(data.current_session_id);
                setSessions((prevData) => [...(prevData || []), ...formattedSessions]);
            } else {
                setEnd(true);
            }
            setLoading(false);
        } catch (err: any) {
            const messageError = errorMessage(err);
            setMessage(messageError, "error");
            console.error("Erro carregar dados: ", messageError);
            console.error("Erro carregar dados: ", err);
            setEnd(true)
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const modalBody = document.getElementById("modal-comments-body");

        const handleScroll = () => {
            if (!modalBody) return;
            const { scrollTop, clientHeight, scrollHeight } = modalBody;

            if ((scrollTop + clientHeight >= scrollHeight - 50) && !end && !loading) {
                setPage((prevPage) => prevPage + 1); // Agora a página é atualizada antes da requisição
            }
        };

        if (modalBody) {
            modalBody.addEventListener("scroll", handleScroll);
        }

        return () => {
            if (modalBody) {
                modalBody.removeEventListener("scroll", handleScroll);
            }
        };
    }, [end, loading]);

    useEffect(() => {
        if (canManageSecurity) {
            loadData(page);
        }
    }, [canManageSecurity, page]);

    const observerCallback = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const target = entries[0];
            if (target.isIntersecting && !loading && !end) {}
        },
        [loading, end]
    );

    useEffect(() => {
        if (endOfListRef.current) {
            observerRef.current = new IntersectionObserver(observerCallback, {
                root: null,
                rootMargin: "200px",
                threshold: 0.1
            });

            observerRef.current.observe(endOfListRef.current);
        }

        return () => observerRef.current?.disconnect();
    }, [observerCallback]);

    useEffect(() => {
        // Salva a posição de rolagem quando o feed for modificado
        const handleScroll = () => {
            sessionStorage.setItem("scrollPosition", window.scrollY.toString());
        };

        window.addEventListener("scroll", handleScroll)

        return () => {
            window.removeEventListener("scroll", handleScroll)
        };
    }, []);

    const confirmDeleteSession = async (password: string) => {
        if (!canManageSecurity) return;

        setIsLoading(true)
        try {
            if (!password) {
                setErrorModalPassword("Informe sua senha")
                setIsLoading(false)
                return;
            }

            await axios.post(`${PACOCA_API_URL}/logout/auth`, {
                token: sessionToDelete?.token, password
            }, {
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            setShowConfirmation(false)
            setMessage(t("settings.sessions.removed"), "success");

            setTimeout(() => {
                if (sessionToDelete?.token == user?.token)
                    logout(true, false, true)
            }, 500)
            setSessions(prevSessions => prevSessions ? prevSessions.filter(s => s.token !== sessionToDelete?.token) : []);

        } catch (err: any) {
            const messageError = errorMessage(err)
            setErrorModalPassword(messageError)
            console.error('Erro ao deletar sessão:', err);
        } finally {
            setPassword("");
            setIsLoading(false)
        }
    }

    const onClose = () => {
        setPassword("");
        setShowConfirmation(false)
    }

    if (!canManageSecurity) {
        return (
            <Screen paddingTopTitle={true} title={t("settings.myDevices")}>
                <div className="ses-list">
                    <div className="ses-card">
                        <div className="ses-card-body">{t("settings.securityRestricted")}</div>
                    </div>
                </div>
            </Screen>
        );
    }

    return (
        <>
            <ConfirmationModal
                show={showConfirmation}
                onClose={() => onClose()}
                onConfirm={() => confirmDeleteSession(password)}
                title={t("settings.sessions.confirmRemove")}
                text={t("settings.sessions.deviceLoggedOut")}
                requirePassword={true}
                setPassword={setPassword}
                errorMessage={errorModal}
                isLoading={isLoading}
                closeRef={closeRef}
            />

            <Screen paddingTopTitle={true} title={t("settings.myDevices")}>
                <Helmet>
                    <title>Paçoca - {t("settings.myDevices")}</title>
                    <meta name="description" content="Paçoca - Veja todos os dispositivos conectados à sua conta." />
                    <meta name="keywords" content="Paçoca, rede social brasileira, sessões, dispositivos, segurança" />
                    <meta name="author" content="Paçoca Inc." />
                    <meta property="og:title" content="Paçoca - Meus Dispositivos" />
                    <meta property="og:description" content="Gerencie os dispositivos conectados à sua conta Paçoca." />
                    <meta property="og:image" content="/img/link-preview.png?v=1" />
                    <meta property="og:url" content={window.location.href} />
                    <meta property="og:type" content="website" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content="Paçoca - Meus Dispositivos" />
                    <meta name="twitter:description" content="Gerencie os dispositivos conectados à sua conta Paçoca." />
                    <meta name="twitter:image" content="/img/link-preview.png?v=1" />
                </Helmet>

                <div className="ses-list">
                    {sessions
                        ?.slice()
                        .sort((a, b) => (a.id === currentSessionId ? -1 : b.id === currentSessionId ? 1 : 0))
                        .map((session, index) => {
                            const isExpanded = expandedIds.has(session.id);
                            const isCurrent = session.id === currentSessionId;
                            return (
                                <div key={session.id ?? index} className={`ses-card ${isCurrent ? 'ses-card-current' : ''}`}>
                                    {/* Header row */}
                                    <div className="ses-card-header" onClick={() => toggleExpand(session.id)}>
                                        <div className="ses-device-icon-wrap">
                                            {getDeviceIcon(session.platform ?? '')}
                                        </div>

                                        <div className="ses-card-name">
                                            <span className="ses-platform">{session.platform}</span>
                                            {isCurrent && (
                                                <span className="ses-current-badge">
                                                    {t("settings.sessions.thisDevice")}
                                                </span>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            className="ses-delete-btn"
                                            title={t("settings.sessions.confirmRemove")}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setsessionToDelete(session);
                                                setShowConfirmation(true);
                                            }}
                                            onTouchEnd={(e) => {
                                                e.stopPropagation();
                                                setsessionToDelete(session);
                                                setShowConfirmation(true);
                                            }}
                                        >
                                            <Trash2 size={16} />
                                        </button>

                                        <button type="button" className="ses-expand-btn" aria-label="Expandir">
                                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        </button>
                                    </div>

                                    {/* Expanded detail */}
                                    {isExpanded && (
                                        <div className="ses-card-body">
                                            <div className="ses-info-grid">
                                                <div className="ses-info-item">
                                                    <Globe size={13} className="ses-info-icon" />
                                                    <span className="ses-info-label">IP</span>
                                                    <span className="ses-info-value">{session.ip_address || '—'}</span>
                                                </div>
                                                <div className="ses-info-item">
                                                    <MapPin size={13} className="ses-info-icon" />
                                                    <span className="ses-info-label">{t("settings.sessions.location")}</span>
                                                    <span className="ses-info-value">
                                                        {[session.city, session.region_name, session.country].filter(Boolean).join(', ') || '—'}
                                                    </span>
                                                </div>
                                                {session.browser && (
                                                    <div className="ses-info-item">
                                                        <Monitor size={13} className="ses-info-icon" />
                                                        <span className="ses-info-label">{t("settings.sessions.browser")}</span>
                                                        <span className="ses-info-value">{session.browser}</span>
                                                    </div>
                                                )}
                                                <div className="ses-info-item">
                                                    <Clock size={13} className="ses-info-icon" />
                                                    <span className="ses-info-label">{t("settings.sessions.lastLogin")}</span>
                                                    <span className="ses-info-value">
                                                        {format(new Date(session.updated_at), "dd/MM/yyyy - HH:mm:ss")}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                    <div ref={endOfListRef} />
                </div>

                {loading && (
                    <div className="d-flex" style={{ justifyContent: "center", marginTop: "40px" }}>
                        <LoadingThreeCircle />
                    </div>
                )}
            </Screen>
        </>
    );
};

export default Sessions;